import type { TTSAudioData, TTSAudioSplit } from '../types/online-data';

// 只移除两端的
// 空白及常见包裹符号，句号、逗号等正文标点仍参与逐字时间计算。
const BOUNDARY_PREFIX_PATTERN = /^[\s"'《》<>[\]【】()（）{}]+/;
const BOUNDARY_SUFFIX_PATTERN = /[\s"'《》<>[\]【】()（）{}]+$/;

function toFiniteTime(value: unknown): number | undefined {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return undefined;
  }

  const time = Number(value);
  return Number.isFinite(time) ? time : undefined;
}

function clampTime(value: number, duration: number): number {
  return Math.min(duration, Math.max(0, value));
}

/** 去掉文本两端空白与常见包裹符号，正文标点保留 */
function trimBoundary(text: string): string {
  return text.replace(BOUNDARY_PREFIX_PATTERN, '').replace(BOUNDARY_SUFFIX_PATTERN, '');
}

/**
 * 向前查找可用的时间边界，供当前片段补全 beginTime。
 * 步骤：
 * 1. 从 index 前一条开始倒序遍历
 * 2. 优先取该条 endTime
 * 3. 非 stop 且无 endTime 时，退而使用其 beginTime
 */
function getPreviousBoundary(splits: TTSAudioSplit[], index: number): number | undefined {
  for (let current = index - 1; current >= 0; current -= 1) {
    const split = splits[current];

    const endTime = toFiniteTime(split.endTime);
    if (endTime !== undefined) return endTime;

    if (split.type !== 'stop') {
      const beginTime = toFiniteTime(split.beginTime);
      if (beginTime !== undefined) return beginTime;
    }
  }

  return undefined;
}

/**
 * 向后查找晚于 beginTime 的时间边界，供当前片段补全 endTime。
 * 步骤：
 * 1. 从 index 后一条开始正序遍历
 * 2. stop 取 endTime，其余取 beginTime
 * 3. 仅返回严格大于 beginTime 的边界，避免零长度区间
 */
function getNextBoundary(
  splits: TTSAudioSplit[],
  index: number,
  beginTime: number
): number | undefined {
  for (let current = index + 1; current < splits.length; current += 1) {
    const split = splits[current];

    const boundary =
      split.type === 'stop' ? toFiniteTime(split.endTime) : toFiniteTime(split.beginTime);
    if (boundary !== undefined && boundary > beginTime) return boundary;
  }

  return undefined;
}

/**
 * 基于服务端下发的 splits 补全/校正每段文本的起止时间。
 * 步骤：
 * 1. 跳过 stop（无高亮文本，但可作为相邻片段的时间锚点）
 * 2. 推断 beginTime：自身值 → 前序边界 → 上一条已输出文本的结束时间，且不早于 previousEndTime
 * 3. 推断 endTime：自身值 → 后续边界 → 音频总时长
 * 4. 钳制到合法区间；空文本或 endTime <= beginTime 的片段丢弃
 */
function buildFromProvidedSplits(audio: TTSAudioData, duration: number): TTSAudioSplit[] {
  const sourceSplits = audio.splits ?? [];
  const splits: TTSAudioSplit[] = [];
  let previousEndTime = 0;

  sourceSplits.forEach((split, index) => {
    // stop 不对应可高亮文本，但它的 endTime 是相邻文本片段的重要时间边界。
    if (split.type === 'stop') return;

    const text = split.text;
    // beginTime 缺失时依次使用前一条记录的边界、上一条已输出文本的结束时间。
    const ownBeginTime = toFiniteTime(split.beginTime);
    const inferredBeginTime = getPreviousBoundary(sourceSplits, index) ?? previousEndTime;
    const beginTime = clampTime(
      Math.max(previousEndTime, ownBeginTime ?? inferredBeginTime),
      duration
    );

    // endTime 缺失时使用下一条记录的开始边界（stop 使用 endTime），末条延伸到音频结束。
    const ownEndTime = toFiniteTime(split.endTime);
    const inferredEndTime = getNextBoundary(sourceSplits, index, beginTime) ?? duration;
    const endTime = clampTime(ownEndTime ?? inferredEndTime, duration);

    if (endTime > beginTime) previousEndTime = endTime;
    if (!text.trim() || endTime <= beginTime) return;

    splits.push({ type: split.type, text, beginTime, endTime });
  });

  return splits;
}

/**
 * 无有效 splits 时，按字符均分整段音频时长。
 * 步骤：
 * 1. 去掉两端包裹符号，得到用于计时的有效文本
 * 2. 用 Array.from 按 Unicode 码点拆字，避免拆坏代理对
 * 3. 按 UTF-16 length 累加 offset，代理对字符占两个码元对应的时长
 */
function buildAverageSplits(audio: TTSAudioData, duration: number): TTSAudioSplit[] {
  const effectiveText = trimBoundary(audio.text);
  if (!effectiveText || duration <= 0) return [];

  // Array.from 保证 chunk.text 不会拆成无效的代理项，时间仍按其 UTF-16 下标推进。
  const unitDuration = duration / effectiveText.length;
  let offset = 0;

  return Array.from(effectiveText, (text) => {
    const beginTime = offset * unitDuration;
    offset += text.length;

    return {
      type: 'chunk',
      text,
      beginTime,
      endTime: offset * unitDuration
    };
  });
}

/**
 * 构建 TTS 逐字/分段高亮所需的时间轴 splits。
 * 步骤：
 * 1. 校验文本与时长，非法则返回空数组
 * 2. 优先用服务端 splits 补全时间；补全后若仍为空则降级
 * 3. 降级为按字符均分时长
 */
export function buildTTSAudioSplits(audio: TTSAudioData): TTSAudioSplit[] {
  const duration = Math.max(0, toFiniteTime(audio.duration) ?? 0);
  if (!audio.text || duration <= 0) return [];

  if (audio.splits?.length) {
    const splits = buildFromProvidedSplits(audio, duration);
    if (splits.length) return splits;
  }

  return buildAverageSplits(audio, duration);
}
